import React, { ReactNode } from 'react';
import { Box, Checkbox, Flex, Text, Tooltip } from '@fullstory/components';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConditionalWrapper } from '@fullstory/components/components/ConditionalWrapper';

export const testIds = {
  checkboxWeb: 'multi-platform-select-checkbox-web',
  checkboxIOS: 'multi-platform-select-checkbox-ios',
  checkboxAndroid: 'multi-platform-select-checkbox-android',
  checkboxApi: 'multi-platform-select-checkbox-api',
};

type MultiPlatformSelectionProps = {
  platforms: Set<NativePlatform>;
  onChange: (platforms: Set<NativePlatform>) => void;
  disabled?: boolean;
  showError: boolean;
  /** iOS tooltip content to display when option is disabled. */
  iosDisabledTooltip?: ReactNode | string;
  /** Android tooltip content to display when option is disabled. */
  androidDisabledTooltip?: ReactNode | string;
  /** If the "Platform" label should have a bold weight. Default is false. */
  boldLabel?: boolean;
};

export const MultiPlatformSelection = ({
  platforms,
  onChange,
  disabled = false,
  showError,
  boldLabel,
  iosDisabledTooltip,
  androidDisabledTooltip,
}: MultiPlatformSelectionProps) => {
  const intl = useIntl();

  const onPlatformChange = (platform: NativePlatform) => () => {
    const updatedPlatforms = new Set(platforms);

    if (platforms.has(platform)) updatedPlatforms.delete(platform);
    else updatedPlatforms.add(platform);
    onChange(updatedPlatforms);
  };

  return (
    <Box data-component="MultiPlatformSelection" minWidth={240}>
      <Flex justifyContent="space-between">
        <Text appearance={boldLabel ? 'bodyMediumBold' : 'bodyMedium'}>
          <FormattedMessage defaultMessage="Platform" id="Mmuj1R" />
        </Text>
        <Flex column>
          <Flex>
            <Box marginRight={20}>
              <Checkbox
                label={intl.formatMessage({ defaultMessage: 'Web', id: 'B6CB37' })}
                checked={platforms.has(NativePlatform.NATIVE_PLATFORM_WEB)}
                onChange={onPlatformChange(NativePlatform.NATIVE_PLATFORM_WEB)}
                testId={testIds.checkboxWeb}
                disabled={disabled}
              />
            </Box>
            <Box marginRight={20}>
              <ConditionalWrapper
                condition={disabled && !!iosDisabledTooltip}
                wrapper={children => (
                  <Tooltip tooltipContent={iosDisabledTooltip}>{children}</Tooltip>
                )}
              >
                <Checkbox
                  label={intl.formatMessage({ defaultMessage: 'iOS', id: 'OQDKnx' })}
                  checked={platforms.has(NativePlatform.NATIVE_PLATFORM_IOS)}
                  onChange={onPlatformChange(NativePlatform.NATIVE_PLATFORM_IOS)}
                  testId={testIds.checkboxIOS}
                  disabled={disabled}
                />
              </ConditionalWrapper>
            </Box>
            <Box>
              <ConditionalWrapper
                condition={disabled && !!androidDisabledTooltip}
                wrapper={children => (
                  <Tooltip tooltipContent={androidDisabledTooltip}>{children}</Tooltip>
                )}
              >
                <Checkbox
                  label={intl.formatMessage({ defaultMessage: 'Android', id: 'gQ7kPU' })}
                  checked={platforms.has(NativePlatform.NATIVE_PLATFORM_ANDROID)}
                  onChange={onPlatformChange(NativePlatform.NATIVE_PLATFORM_ANDROID)}
                  testId={testIds.checkboxAndroid}
                  disabled={disabled}
                />
              </ConditionalWrapper>
            </Box>
          </Flex>
          {showError && platforms.size == 0 && (
            <Box marginTop={8} testId="platform-validation-error">
              <Text appearance="bodySmall" color="destructive">
                <FormattedMessage
                  defaultMessage="At least one platform must be selected"
                  id="VPDOfh"
                />
              </Text>
            </Box>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
